import { useEffect, useState, useCallback, useMemo } from "react";
import { DataTable } from "..";
import { getAllLocations, deleteLocation } from "../../api/locations";
import { ILocation } from "../../models/ILocation";
import { Plus, Link } from "@mynaui/icons-react";
import moment from "moment";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useDebouncedSearch } from "../../hooks";
import LocationDialog from "../LocationDialog";
import DeleteLocationDialog from "../DeleteLocationDialog";
import { Button, Card, Loader } from "../../ui";

type Props = {
  canManage: boolean;
};

const LocationsManagement = ({ canManage }: Props) => {
  const dispatch = useAppDispatch();
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<ILocation | undefined>();
  const [locationToDelete, setLocationToDelete] = useState<ILocation | undefined>();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const { searchValue, setSearchValue } = useDebouncedSearch(handleSearch, 500, "");

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAllLocations();
      setLocations(data || []);
    } catch (err: any) {
      if (err?.message === "No locations found") setLocations([]);
      else dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to load locations", type: "error" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleCreate = () => {
    setSelectedLocation(undefined);
    setIsLocationDialogOpen(true);
  };

  const handleEdit = (location: ILocation) => {
    setSelectedLocation(location);
    setIsLocationDialogOpen(true);
  };

  const handleDelete = (location: ILocation) => {
    setLocationToDelete(location);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!locationToDelete) return;
    try {
      await deleteLocation(locationToDelete.id);
      dispatch(setGeneralAlertMessage({ message: "Location deleted successfully", type: "success" }));
      setLocationToDelete(undefined);
      await fetchLocations();
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.message || "Failed to delete location", type: "error" }));
      throw error;
    }
  };

  const handleDialogClose = async (update?: boolean) => {
    setIsLocationDialogOpen(false);
    setSelectedLocation(undefined);
    if (update) await fetchLocations();
  };

  const filteredLocations = useMemo(() => {
    let filtered = locations.filter((location) => {
      if (!search) return true;
      return location.name.toLowerCase().includes(search.toLowerCase());
    });

    filtered = [...filtered].sort((a, b) => {
      let aVal: any = a[sortColumn as keyof ILocation];
      let bVal: any = b[sortColumn as keyof ILocation];

      if (sortColumn === "name") { aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); }
      else if (sortColumn === "createdAt") { aVal = new Date(a.createdAt).getTime(); bVal = new Date(b.createdAt).getTime(); }

      return sortDirection === "asc"
        ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0)
        : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
    });

    return filtered;
  }, [locations, search, sortColumn, sortDirection]);

  const pageSize = 10;
  const totalPages = Math.ceil(filteredLocations.length / pageSize);
  const paginated = filteredLocations.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Card
      className="locations-management"
      title="Locations"
      subtitle={`${filteredLocations.length} ${filteredLocations.length === 1 ? "location" : "locations"}`}
      actions={
        canManage && (
          <Button icon={<Plus size={16} />} onClick={handleCreate}>
            Add Location
          </Button>
        )
      }
    >
      {loading && locations.length === 0 ? (
        <Loader />
      ) : (
        <DataTable
          id="locations-table"
          data={paginated}
          editable
          label="locations"
          pageNumber={page}
          pageSize={pageSize}
          totalPages={totalPages}
          totalItems={filteredLocations.length}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onViewItem={handleEdit}
          onPageChange={setPage}
          onSortChange={(col, dir) => { setSortColumn(col); setSortDirection(dir); setPage(1); }}
          canDelete={canManage}
          onDelete={handleDelete}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          loading={loading}
          columns={[
            { key: "name", label: "Name", sortable: true },
            {
              key: "googleMapsLink", label: "Google Maps",
              render: (_: any, row: ILocation) => row.googleMapsLink ? (
                <a
                  href={row.googleMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="maps-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Link size={16} /> View Map
                </a>
              ) : <span className="not-set">Not set</span>,
            },
            {
              key: "createdAt", label: "Created At", sortable: true,
              render: (_: any, row: ILocation) => moment(row.createdAt).format("DD MMM, YYYY"),
            },
          ]}
        />
      )}

      {isLocationDialogOpen && (
        <LocationDialog location={selectedLocation} isOpen={isLocationDialogOpen} onClose={handleDialogClose} />
      )}

      {isDeleteDialogOpen && locationToDelete && (
        <DeleteLocationDialog
          locationName={locationToDelete.name}
          isOpen={isDeleteDialogOpen}
          onDelete={handleDeleteConfirm}
          onClose={(update) => {
            setIsDeleteDialogOpen(false);
            if (update) setLocationToDelete(undefined);
          }}
        />
      )}
    </Card>
  );
};

export default LocationsManagement;
